import { defineRule, configure } from 'vee-validate';
import i18n from '@/i18n';
import { useUserStore } from '@/store/modules/user';

defineRule('required', (value: unknown) => {
  if (value === undefined || value === null || value === '') {
    return 'This field is required';
  }
  if (Array.isArray(value) && value.length === 0) {
    return 'This field is required';
  }
  return true;
});

defineRule('email', (value: unknown) => {
  if (!value) return true;
  const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return re.test(String(value)) || 'Invalid email';
});

defineRule('numeric', (value: unknown) => {
  if (value === undefined || value === null || value === '') return true;
  return /^[0-9]+$/.test(String(value)) || 'Must be numeric';
});

defineRule('min_value', (value: number | string, [min]: [number]) => {
  if (value === undefined || value === null || value === '') return true;
  return Number(value) >= min || `Must be at least ${min}`;
});

defineRule('max_value', (value: number | string, [max]: [number]) => {
  if (value === undefined || value === null || value === '') return true;
  return Number(value) <= max || `Must be at most ${max}`;
});

defineRule('length', (value: string, [len]: [number]) => {
  if (!value) return true;
  return value.length === len || `Must be exactly ${len} characters`;
});

// Cross-field: `confirmed:OtherFieldVid` resolves OtherFieldVid via
// vee-validate 4's native form context (ctx.form, populated when the field
// is inside a `<Form>` / useForm()).
defineRule(
  'confirmed',
  (value: unknown, [target]: [string], ctx?: { form?: Record<string, unknown> }) => {
    if (ctx?.form && target in ctx.form) {
      return value === ctx.form[target] || 'Does not match';
    }
    return value === target || 'Does not match';
  }
);

// Cross-field: `required_if:OtherFieldVid` — required only if OtherField has a
// non-empty value. Used for password-confirm UX where the confirm field is
// only required when the password field is filled in.
defineRule(
  'required_if',
  (value: unknown, [target]: [string], ctx?: { form?: Record<string, unknown> }) => {
    const otherValue =
      ctx?.form && target in ctx.form ? ctx.form[target] : undefined;
    if (otherValue === undefined || otherValue === null || otherValue === '') {
      return true;
    }
    if (value === undefined || value === null || value === '') {
      return 'This field is required';
    }
    return true;
  }
);

defineRule('regex', (value: unknown, [pattern]: [string | RegExp]) => {
  if (!value) return true;
  const re = pattern instanceof RegExp ? pattern : new RegExp(pattern);
  return re.test(String(value)) || 'Invalid format';
});

// File size in KB — `size:2048` rejects files larger than 2048KB.
defineRule('size', (value: unknown, [maxKB]: [number]) => {
  if (!value) return true;
  if (value instanceof File) {
    return value.size <= maxKB * 1024 || `File size must not exceed ${maxKB}KB`;
  }
  return true;
});

defineRule('checkUsernameExists', async (value: string) => {
  if (!value) return true;
  const taken = await useUserStore().checkUsername(value);
  return !taken || 'User with this username already exists';
});

configure({
  generateMessage: ({ field, rule }) => {
    if (!rule) return 'Invalid';
    const key = `validations.messages.${rule.name}`;
    const localized = i18n.global.t(key, { _field_: field, ...rule.params });
    return localized === key ? 'Invalid' : localized;
  }
});
